import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import NotificationBell from './NotificationBell';
import CoalRoundBanner from './CoalRoundBanner';
import ChangePasswordDialog from './ChangePasswordDialog';
import ChangePinDialog from './ChangePinDialog';
import ServiceRequestListener from './ServiceRequestListener';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  LayoutDashboard, ShoppingCart, UtensilsCrossed, Grid3X3, Package,
  Truck, Building2, Landmark, Users, Trophy, MessageSquare, ShieldAlert,
  TrendingUp, Eye, Trash2, MapPin, LogOut, Menu, Wine, Zap, ClipboardList,
  UserCog, Calendar, Clock, CalendarDays, FileSpreadsheet, Settings2, KeyRound, ShieldCheck, Wrench, BookOpen, PercentSquare
} from 'lucide-react';

const navSections = [
  {
    items: [
      { path: '/dashboard', label: 'Overview', icon: LayoutDashboard, end: true },
      // Bartender shares manager rights for live orders / tables but their
      // home panel is /bar (prep queue + recipes). Surface a one-click hop
      // back so they aren't trapped in the admin dashboard.
      { path: '/bar', label: 'Bar Panel', icon: Wine, bartenderOnly: true, end: true },
    ]
  },
  {
    title: 'Operations',
    // Live Orders + Tables are also available to bartenders (they're
    // customer-facing during checkouts). Reservations + Menu stay
    // manager-only at the item level below.
    managerOrBartender: true,
    items: [
      { path: '/dashboard/orders', label: 'Live Orders', icon: ShoppingCart },
      { path: '/dashboard/reservations', label: 'Reservations', icon: CalendarDays, managerOnly: true },
      { path: '/dashboard/menu', label: 'Menu', icon: UtensilsCrossed, managerOnly: true },
      { path: '/dashboard/tables', label: 'Tables', icon: Grid3X3 },
    ]
  },
  {
    title: 'Stock',
    managerOnly: true,
    items: [
      { path: '/dashboard/inventory', label: 'Inventory', icon: Package },
      // Bartender / chef / shisha_expert can draft restock POs for their
      // own category — manager approves before sending to the supplier.
      { path: '/dashboard/purchase-orders', label: 'Purchase Orders', icon: Truck, poStaff: true },
      { path: '/dashboard/suppliers', label: 'Suppliers', icon: Building2 },
      { path: '/dashboard/till', label: 'Till', icon: Landmark },
    ]
  },
  {
    title: 'Team',
    managerOnly: true,
    items: [
      { path: '/dashboard/staff', label: 'Staff', icon: Users },
      { path: '/dashboard/customers', label: 'Customers', icon: UserCog },
      { path: '/dashboard/performance', label: 'Performance', icon: Trophy },
      { path: '/dashboard/feedback', label: 'Feedback', icon: MessageSquare },
      { path: '/dashboard/damages', label: 'Damages', icon: ShieldAlert },
    ]
  },
  {
    title: 'Insights',
    managerOnly: true,
    items: [
      { path: '/dashboard/analytics', label: 'Analytics', icon: TrendingUp },
      { path: '/dashboard/discounts', label: 'Discounts', icon: PercentSquare },
      { path: '/dashboard/audit', label: 'Audit Log', icon: Eye },
      { path: '/dashboard/reports', label: 'Reports', icon: ClipboardList },
      { path: '/dashboard/hard-delete', label: 'Hard Delete', icon: Trash2, directorOnly: true },
      { path: '/dashboard/locations', label: 'Locations', icon: MapPin, directorOnly: true },
    ]
  },
  {
    title: 'Staff Management',
    items: [
      { path: '/dashboard/staff-management', label: 'Staff Hub', icon: UserCog, managerOnly: true },
      { path: '/dashboard/roster', label: 'Roster', icon: Calendar },
      { path: '/dashboard/clock', label: 'Clock In/Out', icon: Clock },
      { path: '/dashboard/leave', label: 'Leave & Swaps', icon: CalendarDays },
      { path: '/dashboard/timesheets', label: 'Timesheets', icon: FileSpreadsheet, managerOnly: true },
    ]
  },
  {
    title: 'Quick Links',
    items: [
      { path: '/dashboard/quick-links', label: 'Quick Links', icon: Zap },
    ]
  },
  {
    title: 'Configuration',
    managerOnly: true,
    items: [
      { path: '/dashboard/settings', label: 'Settings', icon: Settings2 },
    ]
  },
  {
    title: 'Tech',
    techOnly: true,
    items: [
      { path: '/dashboard/tech', label: 'Tech Panel', icon: Wrench, techOnly: true },
    ]
  },
];

const pageTitles = {
  '/dashboard': 'Overview',
  '/dashboard/orders': 'Live Orders',
  '/dashboard/menu': 'Menu Management',
  '/dashboard/tables': 'Table Management',
  '/dashboard/inventory': 'Inventory',
  '/dashboard/purchase-orders': 'Purchase Orders',
  '/dashboard/suppliers': 'Suppliers',
  '/dashboard/till': 'Till Management',
  '/dashboard/staff': 'Staff',
  '/dashboard/performance': 'Performance',
  '/dashboard/feedback': 'Customer Feedback',
  '/dashboard/damages': 'Damage Claims',
  '/dashboard/analytics': 'Analytics',
  '/dashboard/discounts': 'Staff Discounts',
  '/dashboard/audit': 'Audit Log',
  '/dashboard/hard-delete': 'Hard Delete',
  '/dashboard/locations': 'Locations',
  '/dashboard/quick-links': 'Quick Links',
  '/dashboard/reports': 'Reports',
  '/dashboard/staff-management': 'Staff Management',
  '/dashboard/roster': 'Roster & Scheduling',
  '/dashboard/clock': 'Clock In / Out',
  '/dashboard/leave': 'Leave & Shift Swaps',
  '/dashboard/timesheets': 'Timesheets & Reports',
  '/dashboard/settings': 'Settings & Configuration',
  '/dashboard/tech': 'Tech Panel',
};

// Map navigation paths to module keys (for enable/disable toggling)
const pathToModule = {
  '/dashboard/orders': 'orders',
  '/dashboard/menu': 'menu',
  '/dashboard/tables': 'tables',
  '/dashboard/inventory': 'inventory',
  '/dashboard/purchase-orders': 'purchase_orders',
  '/dashboard/suppliers': 'suppliers',
  '/dashboard/till': 'till',
  '/dashboard/staff': 'staff',
  '/dashboard/performance': 'performance',
  '/dashboard/feedback': 'feedback',
  '/dashboard/damages': 'damages',
  '/dashboard/analytics': 'analytics',
  '/dashboard/discounts': 'analytics',
  '/dashboard/audit': 'audit',
  '/dashboard/reports': 'reports',
  '/dashboard/locations': 'locations',
  '/dashboard/hard-delete': 'hard_delete',
  '/dashboard/staff-management': 'staff_management',
  '/dashboard/roster': 'roster',
  '/dashboard/clock': 'clock',
  '/dashboard/leave': 'leave',
  '/dashboard/timesheets': 'timesheets',
  '/dashboard/quick-links': 'quick_links',
};

function SidebarContent({ user, logout, onNavigate, modules, onOpenChangePassword, onOpenChangePin }) {
  // Pending-PO-approval count — manager/director only. Polls every 60s
  // so the badge stays fresh without overloading the API. Endpoint
  // gracefully returns 0 for staff roles so it's safe to call unconditionally.
  const [pendingPoCount, setPendingPoCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/purchase-orders/pending-approval-count');
        if (!cancelled) setPendingPoCount(data?.count || 0);
      } catch (_e) { /* silent — not all roles have access */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-white/5">
        <div className="flex items-center justify-center">
          <img src="/majnu-logo.png" alt="Majnu" className="h-12 w-auto object-contain" data-testid="sidebar-logo" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
        {navSections.map((section, si) => {
          // "Director-equivalent" roles — any of these acts as the owner role.
          // We broaden the match because legacy databases may have admin@ on
          // 'admin' or 'owner' instead of the canonical 'director'.
          const isDirectorish = ['director', 'admin', 'owner'].includes(user?.role);
          const isManagerRole = user?.role === 'manager' || isDirectorish;
          const isBartenderRole = user?.role === 'bartender';
          // Bartender / chef / shisha_expert can draft restock POs for
          // their own inventory category — the rest of "Stock" stays
          // manager-only. `item.poStaff` opens the PO link to these
          // roles even when its parent section is `managerOnly`.
          const isPoStaffRole = ['bartender', 'chef', 'shisha_expert'].includes(user?.role);
          // Bartender shares live-order ops with the manager (edit, void,
          // refund, take cash, clear tables). They DON'T see menu/staff/
          // insights/etc. — those stay manager-only at the item level.
          const isManagerOrBartender = isManagerRole || isBartenderRole;
          const isTechRole = user?.role === 'tech' || isDirectorish;
          // Section gate: managerOnly sections are normally hidden from
          // staff, BUT if ANY item in the section is `poStaff: true` and
          // the user is a PO-eligible staff role, render the section so
          // we can show JUST that item. The item-level filter below then
          // hides everything else.
          const sectionHasPoStaff = (section.items || []).some(i => i.poStaff);
          if (section.managerOnly && !isManagerRole && !(sectionHasPoStaff && isPoStaffRole)) return null;
          if (section.managerOrBartender && !isManagerOrBartender) return null;
          if (section.techOnly && !isTechRole) return null;
          const visibleItems = section.items.filter(item => {
            if (item.directorOnly && !isDirectorish) return false;
            if (item.bartenderOnly && !isBartenderRole) return false;
            // `poStaff` items: visible to PO staff roles AND managers.
            if (item.poStaff && !isManagerRole && !isPoStaffRole) return false;
            // If the item is `poStaff: true` and the user is staff, the
            // managerOnly check below would normally hide it — short-circuit.
            if (item.managerOnly && !isManagerRole && !item.poStaff) return false;
            // Special-case: when a `managerOnly` parent section forces
            // its children to be hidden from staff, only let `poStaff`
            // items through.
            if (section.managerOnly && !isManagerRole && !item.poStaff) return false;
            if (item.techOnly && !isTechRole) return false;
            // Module feature-flags hide pages from regular staff. Directors
            // / admins / owners ALWAYS see their own admin tools — flags
            // were never meant to lock the owner out of their own dashboard.
            if (!isDirectorish) {
              const modKey = pathToModule[item.path];
              if (modKey && modules && modules[modKey] === false) return false;
            }
            return true;
          });
          if (visibleItems.length === 0) return null;
          return (
          <div key={`nav-section-${section.title || si}`}>
            {section.title && (
              <p className="text-[10px] text-zinc-600 font-sans uppercase tracking-[0.2em] font-bold px-3 pt-4 pb-1.5">{section.title}</p>
            )}
            {visibleItems.map(item => {
              // External link (anything in the side nav pointing at a
              // public page like /menu-display). Open in a new tab so the
              // staff session stays alive on the original tab.
              if (item.external) {
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onNavigate}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] font-sans transition-all duration-200 text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.03]"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.label}</span>
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] font-sans transition-all duration-200 ${
                      isActive
                        ? 'bg-gold/10 text-gold border-l-2 border-gold ml-0'
                        : 'text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.03]'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {/* Pending-approval pill on Purchase Orders for manager/director.
                      Only renders when count > 0 so the sidebar stays clean. */}
                  {item.path === '/dashboard/purchase-orders' && isManagerRole && pendingPoCount > 0 && (
                    <span
                      className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-red-500/90 text-white min-w-[18px] text-center shadow-[0_0_8px_rgba(239,68,68,0.4)] flex-shrink-0"
                      data-testid="po-pending-approval-badge"
                      title={`${pendingPoCount} purchase order${pendingPoCount === 1 ? '' : 's'} awaiting your approval`}
                    >
                      {pendingPoCount > 99 ? '99+' : pendingPoCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-white/5 flex items-center justify-center text-xs font-sans text-zinc-400 font-bold">
            {user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-sans truncate">{user?.name}</p>
            <p className="text-zinc-600 text-xs font-sans capitalize">{user?.role}</p>
          </div>
        </div>
        <button onClick={() => { if (onOpenChangePassword) onOpenChangePassword(); if (onNavigate) onNavigate(); }} className="w-full flex items-center gap-2 px-3 py-2 mt-1 text-zinc-500 hover:text-gold hover:bg-gold/5 rounded-sm transition-colors" data-testid="sidebar-change-password">
          <KeyRound className="w-4 h-4" />
          <span className="text-xs font-sans">Change Password</span>
        </button>
        {/* Manager / director can reset their kiosk PIN (used at the till
            for discount approvals + locked-item override). */}
        {(user?.role === 'manager' || user?.role === 'director') && (
          <button onClick={() => { if (onOpenChangePin) onOpenChangePin(); if (onNavigate) onNavigate(); }} className="w-full flex items-center gap-2 px-3 py-2 mt-1 text-zinc-500 hover:text-gold hover:bg-gold/5 rounded-sm transition-colors" data-testid="sidebar-change-pin">
            <ShieldCheck className="w-4 h-4" />
            <span className="text-xs font-sans">Reset Manager PIN</span>
          </button>
        )}
        <button onClick={() => { if (onNavigate) onNavigate(); logout(); }} className="w-full flex items-center gap-2 px-3 py-2 mt-1 text-zinc-500 hover:text-red-400 hover:bg-red-500/5 rounded-sm transition-colors" data-testid="sidebar-logout">
          <LogOut className="w-4 h-4" />
          <span className="text-xs font-sans">Sign Out</span>
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modules, setModules] = useState({});
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [changePinOpen, setChangePinOpen] = useState(false);
  const pageTitle = pageTitles[location.pathname] || 'Dashboard';

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get('/settings/modules'); if (alive) setModules(data || {}); } catch (err) { console.error('Failed to load modules:', err); }
    };
    load();
    const onUpdate = () => load();
    window.addEventListener('modules-updated', onUpdate);
    return () => { alive = false; window.removeEventListener('modules-updated', onUpdate); };
  }, []);

  return (
    <div className="min-h-screen bg-bg-primary flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-56 bg-bg-secondary border-r border-white/5 flex-col fixed inset-y-0 left-0 z-30">
        <SidebarContent user={user} logout={logout} modules={modules} onOpenChangePassword={() => setChangePwdOpen(true)} onOpenChangePin={() => setChangePinOpen(true)} />
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-screen min-w-0">
        {/* Top Bar */}
        <header className="glass-header sticky top-0 z-20 px-4 lg:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden text-zinc-400 hover:text-white p-1" data-testid="mobile-menu-button">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-bg-secondary border-r border-white/5 w-56 p-0">
                <SidebarContent user={user} logout={logout} modules={modules} onNavigate={() => setMobileOpen(false)} onOpenChangePassword={() => setChangePwdOpen(true)} onOpenChangePin={() => setChangePinOpen(true)} />
              </SheetContent>
            </Sheet>
            <h2 className="text-lg font-serif text-white" data-testid="page-title">{pageTitle}</h2>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto min-w-0">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={changePwdOpen} onOpenChange={setChangePwdOpen} />
      <ChangePinDialog open={changePinOpen} onOpenChange={setChangePinOpen} />
      <ServiceRequestListener />
      <CoalRoundBanner />
    </div>
  );
}
